import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Home, Sparkles, Bot, MessageSquare, ArrowRight, Play, RotateCcw, Shield } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';

interface Message {
    id: number;
    from: string;
    to: string;
    content: string;
    type: 'task' | 'result' | 'thinking';
}

const AGENTS = [
    { id: 'user', name: 'You', emoji: '🦍', color: 'bg-green-500', position: 'left' },
    { id: 'orchestrator', name: 'Orchestrator', emoji: '🎯', color: 'bg-purple-500', position: 'center' },
    { id: 'researcher', name: 'Research Bot', emoji: '🔍', color: 'bg-blue-500', position: 'right-top' },
    { id: 'writer', name: 'Writer Bot', emoji: '✍️', color: 'bg-orange-500', position: 'right-bottom' },
];

export const AiAgents = () => {
    const { completeModule } = useAcademyStore();
    const [completed, setCompleted] = useState(false);
    const [messages, setMessages] = useState<Message[]>([]);
    const [isRunning, setIsRunning] = useState(false);
    const [step, setStep] = useState(0);
    const [task, setTask] = useState('Write a report about gorilla conservation');

    const SIMULATION_STEPS: Message[] = [
        { id: 1, from: 'user', to: 'orchestrator', content: task, type: 'task' },
        { id: 2, from: 'orchestrator', to: 'researcher', content: 'Research gorilla conservation facts', type: 'task' },
        { id: 3, from: 'researcher', to: 'orchestrator', content: '🔍 Thinking...', type: 'thinking' },
        { id: 4, from: 'researcher', to: 'orchestrator', content: 'Found: Mountain gorillas population ~1,000. Main threats: habitat loss, poaching.', type: 'result' },
        { id: 5, from: 'orchestrator', to: 'writer', content: 'Write a report using these facts', type: 'task' },
        { id: 6, from: 'writer', to: 'orchestrator', content: '✍️ Writing...', type: 'thinking' },
        { id: 7, from: 'writer', to: 'orchestrator', content: '📄 Report: "Gorilla Conservation: A Critical Mission"\\n\\nMountain gorillas number approximately 1,000...', type: 'result' },
        { id: 8, from: 'orchestrator', to: 'user', content: '✅ Task Complete! Report generated successfully.', type: 'result' },
    ];

    const runSimulation = async () => {
        if (isRunning) return;

        setIsRunning(true);
        setMessages([]);
        setStep(0);

        for (let i = 0; i < SIMULATION_STEPS.length; i++) {
            await new Promise(resolve => setTimeout(resolve, 1200));
            setMessages(prev => [...prev, { ...SIMULATION_STEPS[i], content: i === 0 ? task : SIMULATION_STEPS[i].content }]);
            setStep(i + 1);
        }

        setIsRunning(false);
        if (!completed) {
            setCompleted(true);
            completeModule('ai-agents', 150, 'badge_ai');
        }
    };

    const reset = () => {
        setMessages([]);
        setStep(0);
        setIsRunning(false);
    };

    const getAgentById = (id: string) => AGENTS.find(a => a.id === id);

    return (
        <div className="min-h-screen bg-slate-900 font-sans text-slate-100">
            {/* Header */}
            <div className="bg-slate-800 border-b border-slate-700 px-8 py-4 flex justify-between items-center sticky top-0 z-30">
                <Link to="/" className="flex items-center gap-2 text-slate-400 font-bold hover:text-purple-400 transition-colors">
                    <Home size={20} /> Dashboard
                </Link>
                <div className="flex items-center gap-4">
                    {completed && <div className="hidden md:flex bg-green-900 text-green-400 px-3 py-1 rounded-full text-xs font-bold items-center gap-1"><Sparkles size={12} /> Module Complete</div>}
                    <div className="bg-purple-900/50 text-purple-400 px-4 py-1 rounded-full text-sm font-bold border border-purple-800">
                        🤖 Robot Squad
                    </div>
                    <div className="bg-slate-700/50 text-slate-400 px-3 py-1 rounded-full text-xs font-bold border border-slate-600 flex items-center gap-1">
                        <Shield size={12} /> Safe Simulation
                    </div>
                </div>
            </div>

            <div className="max-w-6xl mx-auto p-8">
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-black font-comic text-white mb-2">AI Agents Arena</h1>
                    <p className="text-slate-400 max-w-2xl mx-auto">
                        Watch how multiple AI agents collaborate to complete a task. The <span className="text-purple-400 font-bold">Orchestrator</span> coordinates between specialized agents.
                    </p>
                </div>

                {/* Task Input */}
                <div className="bg-slate-800 rounded-2xl p-6 mb-8 border border-slate-700">
                    <label className="text-sm font-bold text-slate-400 block mb-2">Your Task:</label>
                    <div className="flex gap-4">
                        <input
                            type="text"
                            value={task}
                            onChange={e => setTask(e.target.value)}
                            disabled={isRunning}
                            className="flex-1 bg-slate-900 border-2 border-slate-700 rounded-xl px-4 py-3 text-white focus:border-purple-500 outline-none"
                            placeholder="Enter a task for the agents..."
                        />
                        <button
                            onClick={runSimulation}
                            disabled={isRunning || !task}
                            className={`px-6 py-3 rounded-xl font-bold flex items-center gap-2 transition-all ${isRunning ? 'bg-slate-700 text-slate-400 cursor-not-allowed' : 'bg-purple-600 hover:bg-purple-500 text-white'
                                }`}
                        >
                            <Play size={18} /> {isRunning ? 'Running...' : 'Run Agents'}
                        </button>
                        <button onClick={reset} className="px-4 py-3 bg-slate-700 hover:bg-slate-600 rounded-xl transition-colors">
                            <RotateCcw size={18} />
                        </button>
                    </div>
                </div>

                {/* Agents Visualization */}
                <div className="grid grid-cols-4 gap-6 mb-8">
                    {AGENTS.map(agent => (
                        <motion.div
                            key={agent.id}
                            animate={{
                                scale: messages.some(m => m.from === agent.id || m.to === agent.id) ? [1, 1.05, 1] : 1,
                                boxShadow: messages[messages.length - 1]?.from === agent.id ? '0 0 30px rgba(168, 85, 247, 0.5)' : 'none'
                            }}
                            transition={{ duration: 0.3 }}
                            className={`bg-slate-800 rounded-2xl p-6 border-2 ${messages[messages.length - 1]?.from === agent.id ? 'border-purple-500' : 'border-slate-700'
                                } text-center relative`}
                        >
                            <div className={`w-16 h-16 ${agent.color} rounded-2xl flex items-center justify-center text-3xl mx-auto mb-3 shadow-lg`}>
                                {agent.emoji}
                            </div>
                            <h3 className="font-bold text-white">{agent.name}</h3>
                            <p className="text-xs text-slate-500">{agent.id}</p>

                            {/* Activity indicator */}
                            {isRunning && messages[messages.length - 1]?.from === agent.id && (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: [0.5, 1, 0.5] }}
                                    transition={{ repeat: Infinity, duration: 1 }}
                                    className="absolute -top-2 -right-2 w-4 h-4 bg-green-500 rounded-full border-2 border-slate-800"
                                />
                            )}
                        </motion.div>
                    ))}
                </div>

                {/* Message Log */}
                <div className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden">
                    <div className="bg-slate-900 px-6 py-3 border-b border-slate-700 flex items-center gap-2">
                        <MessageSquare size={16} className="text-slate-500" />
                        <span className="font-bold text-slate-400">Agent Communication Log</span>
                        <span className="text-xs text-slate-600 ml-auto">Step {step}/{SIMULATION_STEPS.length}</span>
                    </div>

                    <div className="p-6 min-h-[300px] max-h-[400px] overflow-y-auto space-y-4">
                        <AnimatePresence>
                            {messages.map((msg, i) => {
                                const fromAgent = getAgentById(msg.from);
                                const toAgent = getAgentById(msg.to);
                                return (
                                    <motion.div
                                        key={msg.id}
                                        initial={{ opacity: 0, x: -20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        className="flex items-start gap-4"
                                    >
                                        <div className={`w-10 h-10 ${fromAgent?.color} rounded-xl flex items-center justify-center text-lg flex-shrink-0`}>
                                            {fromAgent?.emoji}
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className="font-bold text-white">{fromAgent?.name}</span>
                                                <ArrowRight size={14} className="text-slate-600" />
                                                <span className="text-slate-400">{toAgent?.name}</span>
                                            </div>
                                            <div className={`text-sm p-3 rounded-xl ${msg.type === 'thinking' ? 'bg-slate-700 text-slate-400 italic' :
                                                msg.type === 'result' ? 'bg-green-900/30 text-green-400 border border-green-800' :
                                                    'bg-slate-700 text-slate-300'
                                                }`}>
                                                {msg.content}
                                            </div>
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </AnimatePresence>

                        {messages.length === 0 && (
                            <div className="text-center text-slate-600 py-12">
                                <Bot size={48} className="mx-auto mb-4 opacity-50" />
                                <p>Click "Run Agents" to start the simulation</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
